import { defineQuery } from 'next-sanity';

import { FRAGMENTS } from './fragments';

/**
 * Product page query. Fetches product document by slug.
 * headingIndustry = industry-style hero (D2Cx). headingProduct = product-style hero.
 * Sections include reused industry types + product-only types.
 */
export const PRODUCT_PAGE_QUERY = defineQuery(`
  *[_type == "product" && slug.current == $slug][0]{
    _id,
    name,
    "slug": slug.current,
    metaData { title, description, indexable },
    headingIndustry {
      title,
      description,
      buttonText,
      buttonLink ${FRAGMENTS.link},
      backgroundImage ${FRAGMENTS.imageWithMetadata},
      backgroundOverlay,
      backgroundPosition ${FRAGMENTS.responsiveValue},
      containerPadding ${FRAGMENTS.responsivePadding},
      contentGap ${FRAGMENTS.responsiveValue},
      contentMaxWidth ${FRAGMENTS.responsiveValue},
      titleMaxWidth ${FRAGMENTS.responsiveValue},
      descriptionStyles {
        mobile { textSize, textColor, maxWidth },
        tablet { textSize, textColor, maxWidth },
        laptop { textSize, textColor, maxWidth },
        desktop { textSize, textColor, maxWidth }
      },
      buttonMargin {
        mobile { marginTop, marginBottom },
        tablet { marginTop, marginBottom },
        laptop { marginTop, marginBottom },
        desktop { marginTop, marginBottom }
      },
      buttonPadding ${FRAGMENTS.responsivePadding},
      buttonStyles {
        textColor,
        mobile { textSize, height },
        tablet { textSize, height },
        laptop { textSize, height },
        desktop { textSize, height }
      },
      buttonVariant
    },
    headingProduct {
      title,
      titleHighlight,
      titleHighlightColor,
      buttonText,
      buttonLink ${FRAGMENTS.link},
      badges[] {
        badgeType,
        positionTop ${FRAGMENTS.responsiveValue},
        positionLeft ${FRAGMENTS.responsiveValue},
        positionRight ${FRAGMENTS.responsiveValue},
        positionBottom ${FRAGMENTS.responsiveValue},
        image ${FRAGMENTS.imageWithMetadata},
        wrapperWidth ${FRAGMENTS.responsiveValue},
        wrapperHeight ${FRAGMENTS.responsiveValue},
        imageWidth ${FRAGMENTS.responsiveValue},
        imageHeight ${FRAGMENTS.responsiveValue},
        wrapperBackgroundColor,
        label,
        textBackgroundColor,
        textBorderColor,
        textColor,
        illustrationWidth ${FRAGMENTS.responsiveValue},
        illustrationHeight ${FRAGMENTS.responsiveValue},
        hideOnMobile,
        hideOnTablet,
        hideOnLaptop,
        hideOnDesktop
      },
      bottomImage ${FRAGMENTS.imageWithMetadata},
      contentMaxWidth ${FRAGMENTS.responsiveValue}
    },
    sections[] {
      _type,
      _key,
      _type == "poweredBySection" => {
        label,
        logo ${FRAGMENTS.imageWithMetadata},
        description,
        backgroundColor,
        containerPadding ${FRAGMENTS.responsivePadding},
        contentMaxWidth ${FRAGMENTS.responsiveValue}
      },
      _type == "ctvPartnersSection" => {
        title,
        text,
        logoSlider {
          logos[] {
            logo ${FRAGMENTS.imageWithMetadata},
            type
          }
        },
        backgroundColor,
        containerPadding ${FRAGMENTS.responsivePadding},
        contentMaxWidth ${FRAGMENTS.responsiveValue},
        contentGap ${FRAGMENTS.responsiveValue},
        textGap ${FRAGMENTS.responsiveValue}
      },
      _type == "testimonialSection" => {
        heading,
        testimonials[] {
          name,
          title,
          text,
          avatar ${FRAGMENTS.imageWithMetadata},
          logo ${FRAGMENTS.imageWithMetadata},
          video,
          thumbnail ${FRAGMENTS.imageWithMetadata}
        },
        partnerLogos[] ${FRAGMENTS.imageWithMetadata},
        backgroundColor,
        containerPadding ${FRAGMENTS.responsivePadding},
        contentMaxWidth ${FRAGMENTS.responsiveValue}
      },
      _type == "contactSection" => {
        title,
        text,
        buttonText,
        buttonLink ${FRAGMENTS.link},
        backgroundImage ${FRAGMENTS.imageWithMetadata},
        imageWidth,
        imageHeight,
        outerContainerPadding ${FRAGMENTS.responsivePadding},
        outerContainerBackgroundColor,
        innerContainerPadding ${FRAGMENTS.responsivePadding},
        innerContainerMaxWidth ${FRAGMENTS.responsiveValue},
        contentGap ${FRAGMENTS.responsiveValue},
        textGap ${FRAGMENTS.responsiveValue},
        textContainerMaxWidth ${FRAGMENTS.responsiveValue},
        titleMaxWidth ${FRAGMENTS.responsiveValue},
        textMaxWidth ${FRAGMENTS.responsiveValue},
        titlePadding ${FRAGMENTS.responsivePadding},
        imagePosition {
          mobile { top, right, maxWidth },
          tablet { top, right, maxWidth },
          laptop { top, right, maxWidth },
          desktop { top, right, maxWidth }
        }
      },
      _type == "featuresCardSection" => {
        heading,
        cardGrid {
          cards[] {
            title,
            text,
            image ${FRAGMENTS.imageWithMetadata},
            imageMobile ${FRAGMENTS.imageWithMetadata},
            style,
            imagePosition { laptop, desktop },
            maxWidth,
            link { href, text, target, rel },
            rowGap
          },
          gap ${FRAGMENTS.responsiveValue}
        },
        backgroundColor,
        containerPadding ${FRAGMENTS.responsivePadding},
        contentMaxWidth ${FRAGMENTS.responsiveValue},
        contentGap ${FRAGMENTS.responsiveValue},
        headingMaxWidth ${FRAGMENTS.responsiveValue}
      },
      _type == "statsWithImageSection" => {
        heading,
        description,
        stats[] { value, label },
        image ${FRAGMENTS.imageWithMetadata},
        backgroundColor,
        containerPadding ${FRAGMENTS.responsivePadding},
        contentMaxWidth ${FRAGMENTS.responsiveValue},
        textContainerMaxWidth ${FRAGMENTS.responsiveValue}
      },
      _type == "twoColumnCardsSection" => {
        heading,
        cards[] {
          title,
          subtitle,
          items[] {
            icon ${FRAGMENTS.imageWithMetadata},
            text
          }
        },
        backgroundColor,
        containerPadding ${FRAGMENTS.responsivePadding},
        contentMaxWidth ${FRAGMENTS.responsiveValue}
      },
      _type == "videoWithStepsSection" => {
        heading,
        headingHighlight,
        headingHighlightColor,
        videoUrl,
        videoAspect,
        steps[] { number, label },
        backgroundImage ${FRAGMENTS.imageWithMetadata},
        backgroundColor,
        containerPadding ${FRAGMENTS.responsivePadding},
        contentMaxWidth ${FRAGMENTS.responsiveValue}
      },
      _type == "headingWithImageSection" => {
        heading,
        image ${FRAGMENTS.imageWithMetadata},
        backgroundColor,
        containerPadding ${FRAGMENTS.responsivePadding},
        contentMaxWidth ${FRAGMENTS.responsiveValue}
      },
      _type == "ctaWithImageSection" => {
        heading,
        headingHighlight,
        headingHighlightColor,
        description,
        subline,
        buttonText,
        buttonLink ${FRAGMENTS.link},
        image ${FRAGMENTS.imageWithMetadata},
        backgroundColor,
        containerPadding ${FRAGMENTS.responsivePadding},
        contentMaxWidth ${FRAGMENTS.responsiveValue}
      },
      _type == "featureSliderSection" => {
        heading,
        cards[] {
          title,
          content,
          icon ${FRAGMENTS.imageWithMetadata}
        },
        containerPadding ${FRAGMENTS.responsivePadding},
        contentMaxWidth ${FRAGMENTS.responsiveValue}
      },
      _type == "audienceCardsSection" => {
        heading,
        subtitle,
        items[] { title, text },
        backgroundColor,
        containerPadding ${FRAGMENTS.responsivePadding},
        contentMaxWidth ${FRAGMENTS.responsiveValue}
      }
    },
    latestResourcesSection {
      header,
      heading,
      featuredResources[]-> ${FRAGMENTS.resourceWithMetadata},
      theme,
      containerPadding ${FRAGMENTS.responsivePadding},
      contentMaxWidth ${FRAGMENTS.responsiveValue},
      headerHeadingGap ${FRAGMENTS.responsiveValue},
      headerHeadingPadding ${FRAGMENTS.responsivePadding},
      gridGap {
        horizontal ${FRAGMENTS.responsiveValue},
        vertical ${FRAGMENTS.responsiveValue}
      },
      gridColumns ${FRAGMENTS.responsiveValue}
    },
    faqSection {
      heading,
      description,
      accordion {
        items[] { title, content, isOpenByDefault },
        gap ${FRAGMENTS.responsiveValue}
      },
      backgroundColor,
      containerPadding ${FRAGMENTS.responsivePadding},
      contentMaxWidth ${FRAGMENTS.responsiveValue},
      contentGap ${FRAGMENTS.responsiveValue},
      headingDescriptionGap ${FRAGMENTS.responsiveValue},
      headingDescriptionMaxWidth ${FRAGMENTS.responsiveValue},
      accordionMaxWidth ${FRAGMENTS.responsiveValue}
    }
  }
`);
